import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import { toast } from 'react-toastify';

import {
  computeCatalogApi,
  computeSpecsApi,
  computeVMsApi,
  modelLabRunnersApi,
  modelLabRunsApi,
} from '../../services/api';
import type {
  ComputeCatalogItem,
  ComputeSpec,
  ComputeSpecCreate,
  ComputeVM,
  Executor,
  ModelLabRun,
  ModelLabRunner,
  VMStatus,
} from '../../types';
import { ConfirmDialog, ErrorAlert, PageHeader } from '../common';
import { getApiErrorMessage } from './utils';

type ServerTab = 'servers' | 'specs' | 'catalog' | 'runs' | 'runners';

interface ComputeSpecFormState {
  id?: number;
  name: string;
  vmType: string;
  executor: Executor;
  timeoutHours: string;
  cooldownMinutes: string;
  maxCostEur: string;
  catalogItemIds: number[];
  cloudType: 'SECURE' | 'COMMUNITY';
  gpuCount: string;
  containerDiskGb: string;
  volumeGb: string;
  volumeMountPath: string;
  interruptible: boolean;
  hourlyCostUsd: string;
  advancedProviderConfig: string;
}

const ACTIVE_VM_STATUSES: VMStatus[] = ['provisioning', 'ready', 'working', 'draining', 'cooldown', 'destroying'];
const TERMINAL_RUN_STATUSES = ['completed', 'failed', 'cancelled', 'expired'];

const emptySpecForm: ComputeSpecFormState = {
  name: '',
  vmType: 'NVIDIA RTX A5000',
  executor: 'gpu',
  timeoutHours: '4',
  cooldownMinutes: '10',
  maxCostEur: '',
  catalogItemIds: [],
  cloudType: 'SECURE',
  gpuCount: '1',
  containerDiskGb: '50',
  volumeGb: '20',
  volumeMountPath: '/workspace',
  interruptible: false,
  hourlyCostUsd: '1',
  advancedProviderConfig: '',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' => {
  if (['ready', 'online', 'completed'].includes(status)) return 'success';
  if (['working', 'running', 'claimed'].includes(status)) return 'primary';
  if (['provisioning', 'queued', 'cooldown', 'draining'].includes(status)) return 'warning';
  if (['failed', 'destroying', 'cancelled', 'expired', 'offline', 'disabled'].includes(status)) return 'error';
  if (status === 'destroyed') return 'default';
  return 'info';
};

const providerConfigLabel = (config?: Record<string, any>) => {
  if (!config || typeof config !== 'object') return 'No provider config';
  const parts = [
    config.gpu_count ? `${config.gpu_count} GPU` : null,
    config.cloud_type || null,
    Array.isArray(config.gpu_type_ids) && config.gpu_type_ids.length ? `${config.gpu_type_ids.length} fallbacks` : null,
    config.cost_per_hour_usd ? `$${config.cost_per_hour_usd}/h` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'Provider config set';
};

const formatUsd = (value?: number | string | null, digits = 2) => {
  if (value == null || value === '') return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `$${amount.toFixed(digits)}`;
};

const serverHourlyLabel = (vm: ComputeVM) => {
  if (vm.provider_price_per_hour_usd) return `$${Number(vm.provider_price_per_hour_usd).toFixed(4)}/h`;
  const hourly = vm.provider_metadata?.cost_per_hour_usd;
  if (hourly) return `$${hourly}/h`;
  return '-';
};

const serverCurrentCostLabel = (vm: ComputeVM) => (
  formatUsd(vm.current_cost_estimate_usd ?? vm.cost_estimate_usd)
);

const serverBilledCostLabel = (vm: ComputeVM) => formatUsd(vm.billed_cost_usd, 4);

const activeRunForVm = (vm: ComputeVM, runs: ModelLabRun[]) => (
  runs.find((run) => run.compute_vm === vm.id && !TERMINAL_RUN_STATUSES.includes(run.status))
);

const specToForm = (spec: ComputeSpec): ComputeSpecFormState => ({
  id: spec.id,
  name: spec.name,
  vmType: spec.vm_type || '',
  executor: spec.executor,
  timeoutHours: String(spec.timeout_hours ?? 4),
  cooldownMinutes: String(spec.cooldown_minutes ?? 10),
  maxCostEur: spec.max_cost_eur == null ? '' : String(spec.max_cost_eur),
  catalogItemIds: Array.from(new Set([
    ...(Array.isArray(spec.catalog_items) ? spec.catalog_items : []),
    ...(Array.isArray(spec.provider_config?.catalog_item_ids) ? spec.provider_config.catalog_item_ids : []),
  ].map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0))),
  cloudType: spec.provider_config?.cloud_type === 'COMMUNITY' ? 'COMMUNITY' : 'SECURE',
  gpuCount: String(spec.provider_config?.gpu_count ?? 1),
  containerDiskGb: String(spec.provider_config?.container_disk_gb ?? 50),
  volumeGb: String(spec.provider_config?.volume_gb ?? 20),
  volumeMountPath: String(spec.provider_config?.volume_mount_path ?? '/workspace'),
  interruptible: Boolean(spec.provider_config?.interruptible),
  hourlyCostUsd: spec.provider_config?.cost_per_hour_usd == null ? '' : String(spec.provider_config.cost_per_hour_usd),
  advancedProviderConfig: '',
});

const formToPayload = (form: ComputeSpecFormState): ComputeSpecCreate => {
  let providerConfig: Record<string, any>;
  try {
    providerConfig = form.advancedProviderConfig.trim() ? JSON.parse(form.advancedProviderConfig) : {};
  } catch {
    throw new Error('Advanced provider config must be valid JSON.');
  }
  const catalogItemIds = Array.from(new Set(form.catalogItemIds));
  providerConfig = {
    ...providerConfig,
    cloud_type: form.cloudType,
    container_disk_gb: Number(form.containerDiskGb || 50),
    volume_gb: Number(form.volumeGb || 0),
    volume_mount_path: form.volumeMountPath || '/workspace',
    interruptible: form.interruptible,
  };
  if (form.executor === 'gpu') {
    providerConfig.gpu_count = Number(form.gpuCount || 1);
  }
  if (form.hourlyCostUsd !== '') {
    providerConfig.cost_per_hour_usd = Number(form.hourlyCostUsd);
  }
  if (catalogItemIds.length) {
    providerConfig.catalog_item_ids = catalogItemIds;
  }

  return {
    name: form.name.trim(),
    compute_mode: 'remote_vm',
    vm_type: form.vmType.trim(),
    executor: form.executor,
    timeout_hours: Number(form.timeoutHours || 4),
    cooldown_minutes: Number(form.cooldownMinutes || 10),
    max_cost_eur: form.maxCostEur === '' ? null : Number(form.maxCostEur),
    catalog_items: catalogItemIds,
    provider_config: providerConfig,
  };
};

const priceLabel = (item: ComputeCatalogItem) => {
  if (item.price_per_hour_usd == null) return 'Price returned at launch';
  return `$${Number(item.price_per_hour_usd).toFixed(4)}/h`;
};

const catalogItemLabel = (item?: ComputeCatalogItem | null) => {
  if (!item) return '-';
  const detail = item.resource_type === 'gpu' && item.gpu_memory_gb ? `${item.gpu_memory_gb}GB` : item.provider_resource_id;
  return `${item.display_name} / ${item.cloud_type} / ${detail}`;
};

const SummaryCard: React.FC<{ label: string; value: React.ReactNode; helper: string }> = ({ label, value, helper }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const ModelLabServersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<ServerTab>('servers');
  const [specDialog, setSpecDialog] = useState<ComputeSpecFormState | null>(null);
  const [terminateVm, setTerminateVm] = useState<ComputeVM | null>(null);
  const [deleteSpec, setDeleteSpec] = useState<ComputeSpec | null>(null);

  const vmsQuery = useQuery({
    queryKey: ['model-lab-compute-vms'],
    queryFn: () => computeVMsApi.list({ page_size: 100 }),
    staleTime: 15000,
    refetchInterval: tab === 'servers' ? 30000 : false,
  });
  const specsQuery = useQuery({
    queryKey: ['model-lab-compute-specs'],
    queryFn: () => computeSpecsApi.list({ compute_mode: 'remote_vm', page_size: 100 }),
    staleTime: 60000,
  });
  const catalogQuery = useQuery({
    queryKey: ['model-lab-compute-catalog'],
    queryFn: () => computeCatalogApi.list({ ordering: 'resource_type,price_per_hour_usd,display_name', page_size: 500 }),
    enabled: tab === 'catalog' || Boolean(specDialog),
    staleTime: 300000,
  });
  const runsQuery = useQuery({
    queryKey: ['model-lab-runs'],
    queryFn: () => modelLabRunsApi.list({ page_size: 50 }),
    enabled: tab === 'servers' || tab === 'runs',
    staleTime: 15000,
    refetchInterval: tab === 'servers' || tab === 'runs' ? 30000 : false,
  });
  const runnersQuery = useQuery({
    queryKey: ['model-lab-runners'],
    queryFn: () => modelLabRunnersApi.list({ page_size: 50 }),
    enabled: tab === 'runners',
    staleTime: 15000,
    refetchInterval: tab === 'runners' ? 30000 : false,
  });

  const invalidateCompute = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['model-lab-compute-vms'] }),
      queryClient.invalidateQueries({ queryKey: ['model-lab-runs'] }),
    ]);
  };
  const refreshCurrentTab = async () => {
    const keysByTab: Record<ServerTab, string[][]> = {
      servers: [['model-lab-compute-vms'], ['model-lab-runs']],
      specs: [['model-lab-compute-specs']],
      catalog: [['model-lab-compute-catalog']],
      runs: [['model-lab-runs']],
      runners: [['model-lab-runners']],
    };
    await Promise.all(
      keysByTab[tab].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  };

  const saveSpecMutation = useMutation({
    mutationFn: async (form: ComputeSpecFormState) => {
      const payload = formToPayload(form);
      if (form.id) return computeSpecsApi.update(form.id, payload);
      return computeSpecsApi.create(payload);
    },
    onSuccess: async () => {
      toast.success('Compute spec saved');
      setSpecDialog(null);
      await queryClient.invalidateQueries({ queryKey: ['model-lab-compute-specs'] });
    },
    onError: (error: Error) => toast.error(getApiErrorMessage(error, 'Could not save compute spec.')),
  });

  const deleteSpecMutation = useMutation({
    mutationFn: async (spec: ComputeSpec) => computeSpecsApi.delete(spec.id),
    onSuccess: async () => {
      toast.success('Compute spec deleted');
      setDeleteSpec(null);
      await queryClient.invalidateQueries({ queryKey: ['model-lab-compute-specs'] });
    },
    onError: (error: Error) => toast.error(getApiErrorMessage(error, 'Could not delete compute spec.')),
  });

  const terminateMutation = useMutation({
    mutationFn: async (vm: ComputeVM) => computeVMsApi.terminate(vm.id),
    onSuccess: async () => {
      toast.success('Server termination requested');
      setTerminateVm(null);
      await invalidateCompute();
    },
    onError: (error: Error) => toast.error(getApiErrorMessage(error, 'Could not terminate compute server.')),
  });

  const refreshCatalogMutation = useMutation({
    mutationFn: () => computeCatalogApi.refresh(),
    onSuccess: async () => {
      toast.success('RunPod catalog refresh requested');
      await queryClient.invalidateQueries({ queryKey: ['model-lab-compute-catalog'] });
    },
    onError: (error: Error) => toast.error(getApiErrorMessage(error, 'Could not refresh RunPod catalog.')),
  });

  const cancelRunMutation = useMutation({
    mutationFn: async (run: ModelLabRun) => modelLabRunsApi.cancel(run.id),
    onSuccess: async () => {
      toast.success('Run cancellation requested');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['model-lab-runs'] }),
        queryClient.invalidateQueries({ queryKey: ['model-lab-compute-vms'] }),
      ]);
    },
    onError: (error: Error) => toast.error(getApiErrorMessage(error, 'Could not cancel Model Lab run.')),
  });

  const vms = vmsQuery.data || [];
  const specs = specsQuery.data || [];
  const catalog = catalogQuery.data || [];
  const runs = runsQuery.data || [];
  const runners = runnersQuery.data || [];
  const specById = useMemo(() => new Map(specs.map((spec) => [spec.id, spec])), [specs]);

  const criticalError = vmsQuery.error || specsQuery.error || (tab === 'catalog' ? catalogQuery.error : null) || (tab === 'runners' ? runnersQuery.error : null);
  const runsError = runsQuery.error;
  const error = criticalError || (tab === 'runs' ? runsError : null);
  if (error) return <ErrorAlert error={error as Error} />;
  const activeVms = vms.filter((vm) => ACTIVE_VM_STATUSES.includes(vm.status));
  const activeRuns = runs.filter((run) => !TERMINAL_RUN_STATUSES.includes(run.status));
  const activeGpuSpecs = specs.filter((spec) => spec.executor === 'gpu');
  const billedComputeUsd = vms.reduce((sum, vm) => sum + (Number(vm.billed_cost_usd || 0) || 0), 0);

  return (
    <Box>
      <PageHeader
        title="Model Lab Servers"
        breadcrumbs={[
          { label: 'Model Lab', to: '/ai/model-lab' },
          { label: 'Servers' },
        ]}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refreshCurrentTab()}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setSpecDialog(emptySpecForm)}
            >
              New Compute Spec
            </Button>
          </Stack>
        }
      />

      {(vmsQuery.isFetching || specsQuery.isFetching || catalogQuery.isFetching || runsQuery.isFetching || runnersQuery.isFetching) && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <Stack spacing={2}>
        <Alert severity="info">
          Servers are created by Model Lab runs from the selected compute spec, then moved to cooldown and destroyed automatically.
          Use this page to monitor active pods, cancel stuck runs, terminate idle servers, and configure RunPod GPU specs.
        </Alert>
        {runsError && tab !== 'runs' ? (
          <Alert severity="warning">
            Server inventory loaded, but recent runner jobs could not be refreshed: {getApiErrorMessage(runsError as Error, 'runs request failed')}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SummaryCard label="Active Servers" value={activeVms.length} helper="Provisioning, ready, working, or cooling down" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SummaryCard label="Active Runs" value={activeRuns.length} helper="Queued, claimed, or running runner jobs" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SummaryCard label="GPU Specs" value={activeGpuSpecs.length} helper="Remote specs available for RunPod launches" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SummaryCard label="AI Credit Billed" value={formatUsd(billedComputeUsd, 4)} helper="Debited Model Lab compute spend" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SummaryCard label="Catalog Items" value={catalog.length} helper="RunPod CPU/GPU options with pricing" />
          </Grid>
        </Grid>

        <Paper>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="servers" label="Servers" icon={<CloudQueueIcon fontSize="small" />} iconPosition="start" />
            <Tab value="specs" label="Compute Specs" />
            <Tab value="catalog" label="Catalog" />
            <Tab value="runs" label="Runs" />
            <Tab value="runners" label="Runners" />
          </Tabs>
        </Paper>

        {tab === 'servers' && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Server</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Spec</TableCell>
                  <TableCell>Run</TableCell>
                  <TableCell>Ready</TableCell>
                  <TableCell>Destroyed</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vmsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Loading compute servers...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : vms.map((vm) => {
                  const run = activeRunForVm(vm, runs);
                  const spec = vm.compute_spec ? specById.get(vm.compute_spec) : null;
                  const canTerminate = vm.status !== 'destroyed' && vm.status !== 'destroying' && !run;
                  return (
                    <TableRow key={vm.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{vm.provider_instance_name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {vm.vm_type || '-'} / {vm.private_ip || 'no IP yet'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={vm.status} color={statusColor(vm.status)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{vm.provider}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {vm.provider_instance_id || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {serverHourlyLabel(vm)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{spec?.name || `#${vm.compute_spec || '-'}`}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {spec ? providerConfigLabel(spec.provider_config) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {run ? (
                          <Stack spacing={0.5}>
                            <Chip size="small" label={run.status} color={statusColor(run.status)} />
                            <Typography variant="caption" color="text.secondary">
                              {run.job_type} #{run.job_id}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No active run</Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(vm.ready_at)}</TableCell>
                      <TableCell>{formatDateTime(vm.destroyed_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{serverCurrentCostLabel(vm)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Billed {serverBilledCostLabel(vm)}
                        </Typography>
                        <Typography variant="caption" color={Number(vm.unbilled_cost_usd || 0) > 0 ? 'warning.main' : 'text.secondary'} display="block">
                          Unbilled {formatUsd(vm.unbilled_cost_usd, 4)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last billed {formatDateTime(vm.last_billed_at)}
                        </Typography>
                        {vm.last_billing_error || vm.last_provider_error ? (
                          <Typography variant="caption" color="error.main" display="block">
                            {vm.last_billing_error || vm.last_provider_error}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {run ? (
                            <Tooltip title="Cancel the active run before terminating the server">
                              <span>
                                <Button
                                  size="small"
                                  color="warning"
                                  startIcon={<StopIcon />}
                                  onClick={() => cancelRunMutation.mutate(run)}
                                  disabled={cancelRunMutation.isPending}
                                >
                                  Cancel Run
                                </Button>
                              </span>
                            </Tooltip>
                          ) : null}
                          <Tooltip title={canTerminate ? 'Terminate this compute server' : 'Server cannot be terminated while a run is active'}>
                            <span>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setTerminateVm(vm)}
                                disabled={!canTerminate || terminateMutation.isPending}
                              >
                                Terminate
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!vmsQuery.isLoading && vms.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No compute servers have been created yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 'specs' && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Executor</TableCell>
                  <TableCell>GPU / Server Type</TableCell>
                  <TableCell>Provider Config</TableCell>
                  <TableCell>Policy</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Loading compute specs...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : specs.map((spec) => (
                  <TableRow key={spec.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{spec.name}</Typography>
                      {spec.is_preset ? <Chip size="small" label="preset" color="info" sx={{ mt: 0.5 }} /> : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={spec.executor.toUpperCase()} color={spec.executor === 'gpu' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{spec.vm_type || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{providerConfigLabel(spec.provider_config)}</Typography>
                      {spec.catalog_item_details?.length ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Catalog: {spec.catalog_item_details.slice(0, 3).map((item) => `#${item.id} ${priceLabel(item)}`).join(' / ')}
                          {spec.catalog_item_details.length > 3 ? ` / +${spec.catalog_item_details.length - 3}` : ''}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        Disk {spec.provider_config?.container_disk_gb || '-'}GB / volume {spec.provider_config?.volume_gb || '-'}GB
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">Timeout {spec.timeout_hours}h</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cooldown {spec.cooldown_minutes}m
                        {spec.max_cost_eur != null ? ` / max EUR ${spec.max_cost_eur}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => setSpecDialog(specToForm(spec))}
                          disabled={spec.is_preset}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setDeleteSpec(spec)}
                          disabled={spec.is_preset}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!specsQuery.isLoading && specs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No remote compute specs are configured yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 'catalog' && (
          <TableContainer component={Paper}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Box>
                <Typography variant="h6">RunPod Compute Catalog</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use these catalog item IDs when creating compute specs. Actual launched price is snapshotted from RunPod.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                disabled={refreshCatalogMutation.isPending}
                onClick={() => refreshCatalogMutation.mutate()}
              >
                Refresh Catalog
              </Button>
            </Stack>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Cloud</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Availability</TableCell>
                  <TableCell>Specs</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Synced</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {catalogQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Loading RunPod catalog...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : catalog.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>#{item.id}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.provider_resource_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{item.display_name}</Typography>
                      <Chip size="small" label={item.resource_type.toUpperCase()} color={item.resource_type === 'gpu' ? 'warning' : 'default'} sx={{ mt: 0.5 }} />
                    </TableCell>
                    <TableCell>{item.cloud_type}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{priceLabel(item)}</Typography>
                      {item.is_price_stale ? <Typography variant="caption" color="warning.main">stale</Typography> : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={item.is_available ? 'available' : 'unavailable'} color={item.is_available ? 'success' : 'default'} />
                      {item.stock_status ? <Typography variant="caption" color="text.secondary" display="block">{item.stock_status}</Typography> : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.resource_type === 'gpu'
                          ? `${item.gpu_memory_gb || '-'}GB VRAM`
                          : `${item.vcpu_count || '-'} vCPU / ${item.memory_gb || '-'}GB`}
                      </Typography>
                      {item.available_gpu_counts?.length ? (
                        <Typography variant="caption" color="text.secondary">
                          GPU counts: {item.available_gpu_counts.join(', ')}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{item.price_source || '-'}</TableCell>
                    <TableCell>{formatDateTime(item.last_synced_at)}</TableCell>
                  </TableRow>
                ))}
                {!catalogQuery.isLoading && catalog.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No catalog rows are available. Refresh the RunPod catalog or run the sync command.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 'runs' && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Spec</TableCell>
                  <TableCell>Runner</TableCell>
                  <TableCell>Server</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Loading runner jobs...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : runs.map((run) => (
                  <TableRow key={run.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{run.job_type} #{run.job_id}</Typography>
                      <Typography variant="caption" color="text.secondary">Run #{run.id}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={run.status} color={statusColor(run.status)} /></TableCell>
                    <TableCell>{run.compute_spec_name || `#${run.compute_spec || '-'}`}</TableCell>
                    <TableCell>{run.runner_name || `#${run.runner || '-'}`}</TableCell>
                    <TableCell>{run.compute_vm_name || `#${run.compute_vm || '-'}`}</TableCell>
                    <TableCell>{formatDateTime(run.started_at)}</TableCell>
                    <TableCell>{formatDateTime(run.completed_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<StopIcon />}
                        disabled={TERMINAL_RUN_STATUSES.includes(run.status) || cancelRunMutation.isPending}
                        onClick={() => cancelRunMutation.mutate(run)}
                      >
                        Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!runsQuery.isLoading && runs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No runner jobs have been created yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 'runners' && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Executor</TableCell>
                  <TableCell>Server</TableCell>
                  <TableCell>Heartbeat</TableCell>
                  <TableCell>Host</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runnersQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        Loading runners...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : runners.map((runner: ModelLabRunner) => (
                  <TableRow key={runner.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{runner.name}</Typography>
                      <Typography variant="caption" color="text.secondary">v{runner.version || '-'}</Typography>
                    </TableCell>
                    <TableCell><Chip size="small" label={runner.status} color={statusColor(runner.status)} /></TableCell>
                    <TableCell>{runner.runner_type}</TableCell>
                    <TableCell>{runner.executor.toUpperCase()}</TableCell>
                    <TableCell>{runner.compute_vm_name || `#${runner.compute_vm || '-'}`}</TableCell>
                    <TableCell>{formatDateTime(runner.last_heartbeat_at)}</TableCell>
                    <TableCell>{runner.hostname || runner.ip_address || '-'}</TableCell>
                  </TableRow>
                ))}
                {!runnersQuery.isLoading && runners.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No runners are registered yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <ComputeSpecDialog
        open={Boolean(specDialog)}
        form={specDialog}
        catalog={catalog}
        isSaving={saveSpecMutation.isPending}
        onChange={setSpecDialog}
        onClose={() => setSpecDialog(null)}
        onSave={(form) => saveSpecMutation.mutate(form)}
      />

      <ConfirmDialog
        open={Boolean(terminateVm)}
        title="Terminate compute server"
        message={`Terminate ${terminateVm?.provider_instance_name || 'this server'}? This calls the provider destroy path and records final compute cost.`}
        confirmText="Terminate"
        confirmColor="error"
        onCancel={() => setTerminateVm(null)}
        onConfirm={() => terminateVm && terminateMutation.mutate(terminateVm)}
      />

      <ConfirmDialog
        open={Boolean(deleteSpec)}
        title="Delete compute spec"
        message={`Delete ${deleteSpec?.name || 'this compute spec'}? Existing server history remains, but this spec will no longer be selectable.`}
        confirmText="Delete"
        confirmColor="error"
        onCancel={() => setDeleteSpec(null)}
        onConfirm={() => deleteSpec && deleteSpecMutation.mutate(deleteSpec)}
      />
    </Box>
  );
};

interface ComputeSpecDialogProps {
  open: boolean;
  form: ComputeSpecFormState | null;
  catalog: ComputeCatalogItem[];
  isSaving: boolean;
  onChange: (form: ComputeSpecFormState | null) => void;
  onClose: () => void;
  onSave: (form: ComputeSpecFormState) => void;
}

const ComputeSpecDialog: React.FC<ComputeSpecDialogProps> = ({ open, form, catalog, isSaving, onChange, onClose, onSave }) => {
  if (!form) return null;

  const update = (patch: Partial<ComputeSpecFormState>) => onChange({ ...form, ...patch });
  const catalogOptions = catalog.filter(
    (item) => item.resource_type === form.executor && item.is_active && item.is_available,
  );
  const selectedCatalogItems = form.catalogItemIds
    .map((id) => catalog.find((item) => item.id === id))
    .filter((item): item is ComputeCatalogItem => Boolean(item));
  const selectedPrices = selectedCatalogItems
    .map((item) => Number(item.price_per_hour_usd))
    .filter((price) => Number.isFinite(price) && price > 0);
  const maxHourly = selectedPrices.length ? Math.max(...selectedPrices) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{form.id ? 'Edit Compute Spec' : 'New Compute Spec'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="RunPod primary type"
                value={form.vmType}
                onChange={(event) => update({ vmType: event.target.value })}
                helperText="GPU type or CPU flavor. Example: NVIDIA RTX A5000 or cpu5c."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Executor"
                value={form.executor}
                onChange={(event) => update({
                  executor: event.target.value as Executor,
                  catalogItemIds: [],
                  vmType: '',
                })}
              >
                <MenuItem value="gpu">GPU</MenuItem>
                <MenuItem value="cpu">CPU</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Timeout hours"
                type="number"
                inputProps={{ min: 0.25, step: 0.25 }}
                value={form.timeoutHours}
                onChange={(event) => update({ timeoutHours: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Cooldown minutes"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={form.cooldownMinutes}
                onChange={(event) => update({ cooldownMinutes: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Max cost EUR"
                type="number"
                inputProps={{ min: 0, step: 0.01 }}
                value={form.maxCostEur}
                onChange={(event) => update({ maxCostEur: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={catalogOptions}
                value={selectedCatalogItems}
                getOptionLabel={(item) => `#${item.id} ${catalogItemLabel(item)} ${priceLabel(item)}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, items) => {
                  const nextPrices = items
                    .map((item) => Number(item.price_per_hour_usd))
                    .filter((price) => Number.isFinite(price) && price > 0);
                  const nextMaxHourly = nextPrices.length ? Math.max(...nextPrices) : null;
                  update({
                    catalogItemIds: items.map((item) => item.id),
                    vmType: items[0]?.provider_resource_id || form.vmType,
                    hourlyCostUsd: nextMaxHourly != null ? String(nextMaxHourly) : form.hourlyCostUsd,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="RunPod catalog items"
                    helperText="Ordered fallback pool used for RunPod provisioning, pricing estimates, and AI-credit preflight."
                  />
                )}
              />
              {selectedCatalogItems.length ? (
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Selected: {selectedCatalogItems.map((item) => `#${item.id} ${catalogItemLabel(item)} ${priceLabel(item)}`).join(' / ')}
                  </Typography>
                  <Typography variant="caption" color={maxHourly == null ? 'warning.main' : 'text.secondary'}>
                    Estimated max hourly price: {maxHourly == null ? 'unknown until launch' : `$${maxHourly.toFixed(4)}/h`}
                  </Typography>
                </Stack>
              ) : null}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="RunPod cloud"
                value={form.cloudType}
                onChange={(event) => update({ cloudType: event.target.value as 'SECURE' | 'COMMUNITY' })}
              >
                <MenuItem value="SECURE">Secure</MenuItem>
                <MenuItem value="COMMUNITY">Community</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="GPU count"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={form.gpuCount}
                onChange={(event) => update({ gpuCount: event.target.value })}
                disabled={form.executor !== 'gpu'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Hourly USD fallback"
                type="number"
                inputProps={{ min: 0, step: 0.0001 }}
                value={form.hourlyCostUsd}
                onChange={(event) => update({ hourlyCostUsd: event.target.value })}
                helperText={maxHourly == null ? 'Required when catalog price is unavailable.' : `Catalog max ${formatUsd(maxHourly, 4)}/h`}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Container disk GB"
                type="number"
                inputProps={{ min: 10, step: 1 }}
                value={form.containerDiskGb}
                onChange={(event) => update({ containerDiskGb: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Volume GB"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={form.volumeGb}
                onChange={(event) => update({ volumeGb: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Volume mount path"
                value={form.volumeMountPath}
                onChange={(event) => update({ volumeMountPath: event.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.interruptible}
                    onChange={(event) => update({ interruptible: event.target.checked })}
                  />
                )}
                label="Use interruptible RunPod instances"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Advanced provider config JSON"
                value={form.advancedProviderConfig}
                onChange={(event) => update({ advancedProviderConfig: event.target.value })}
                helperText="Optional RunPod overrides. Structured fields above win when keys overlap."
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(form)}
          disabled={isSaving || !form.name.trim() || (!form.vmType.trim() && !form.catalogItemIds.length)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelLabServersPage;
